import { ComponentPropsWithoutRef, Fragment, forwardRef, useMemo } from 'react';

import { Typography as Text, makeStyles } from '@material-ui/core';

import { diffChars } from 'diff';

const useStyles = makeStyles({
  addedText: {
    color: 'var(--essential-positive)',
  },
  removedText: {
    color: 'var(--text-subdued)',
    textDecoration: 'line-through',
  },
  modifiedText: {
    color: 'var(--text-base)',
  },
});

export interface DeltaTextProps
  extends Omit<ComponentPropsWithoutRef<typeof Text>, 'children'> {
  oldText: string;
  newText: string;
  disableDiff?: boolean;
}

export const DeltaText = forwardRef<HTMLDivElement, DeltaTextProps>(
  ({ oldText, newText, disableDiff, ...props }, ref) => {
    const classes = useStyles();
    const diff = useMemo(() => diffChars(oldText, newText), [oldText, newText]);

    if (disableDiff) {
      return (
        <Text {...props} ref={ref}>
          <span className={classes.removedText} data-testid="delta-removed">
            {oldText}
          </span>{' '}
          <span className={classes.addedText} data-testid="delta-added">
            {newText}
          </span>
        </Text>
      );
    }

    return (
      <Text {...props} ref={ref}>
        {diff.map((part, index) => (
          <Fragment key={index}>
            {part.added && (
              <span className={classes.addedText} data-testid="delta-added">
                {part.value}
              </span>
            )}
            {part.removed && (
              <span className={classes.removedText} data-testid="delta-removed">
                {part.value}
              </span>
            )}
            {!part.added && !part.removed && <span>{part.value}</span>}
          </Fragment>
        ))}
      </Text>
    );
  },
);
DeltaText.displayName = 'DeltaText';
